import * as React from 'react';
import { useRef } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { translate } from '~l10n/client';
import { Dialog } from '~shared/components/Dialog';
import {
  CurrentQuizEditorDialog,
  currentQuizEditorDialogAtom,
} from '~teacher/components/QuizEditor/atoms/currentQuizEditorDialogAtom';
import { HideIntercomLauncher } from '~teacher/hook/utils/HideIntercomLauncher';

import { StepAtomProvider, generateQuestionAtom } from './atom';
import { QuestionGeneratorContext } from './context/QuestionGeneratorContext';
import { GenerateQuestion } from './GenerateQuestion';
import { addedQuestionSetAtom } from './GenerateQuestionStep2';

interface Props {
  onClose: () => void;
  refreshQuestions: () => Promise<void>;
}

export function GenerateQuestionDialog({ onClose, refreshQuestions }: Props) {
  const currentQuizEditorDialog = useAtomValue(currentQuizEditorDialogAtom);
  const isOpen =
    currentQuizEditorDialog === CurrentQuizEditorDialog.GenerateQuestions;

  // Reset reference from Generate Questions
  const resetRef = useRef<(() => void) | null>(null);
  const resetAddedQuestions = useSetAtom(addedQuestionSetAtom.resetAtom);
  const dispatchGenerateQuestion = useSetAtom(generateQuestionAtom);

  const handleClose = () => {
    // Reset data
    if (resetRef.current) {
      resetRef.current();
    }
    // Reset added questions
    resetAddedQuestions();
    // Clear fileKey avoid including in next generation
    dispatchGenerateQuestion({ type: 'RemoveFileKey' });
    onClose();
  };

  return (
    <Dialog
      position="right"
      showBackdrop={false}
      open={isOpen}
      onClose={handleClose}
      id={'generate-quiz-dialog'}
      size="medium"
      title={translate('Generate Questions')}
      closeLabel={translate('Close')}
      disableBackdropClose
    >
      {() =>
        isOpen ? (
          <StepAtomProvider>
            <QuestionGeneratorContext>
              <GenerateQuestion
                onClose={onClose}
                refreshQuestions={refreshQuestions}
                resetRef={resetRef}
              />
              <HideIntercomLauncher />
            </QuestionGeneratorContext>
          </StepAtomProvider>
        ) : null
      }
    </Dialog>
  );
}
